<template>
  <div v-if="tabs" class="tabs">
    <div class="tabs__head" :class="{ 'tabs__head--shadowed': shadowed }">
      <slot name="tabs" :tab="currentTab">
        <template v-if="type === 'slider'">
          <TabSlider
            v-model:tab="currentTab"
            :tabs="tabs"
            :progress="progress"
            :transition="sliderTransition"
          />
        </template>
        <template v-else-if="type === 'block'">
          <TabBlock v-model:tab="currentTab" :tabs="tabs" />
        </template>
      </slot>
    </div>
    <div class="tabs__body" ref="body">
      <Swiper
        :resistanceRatio="0"
        :initial-slide="currentTab"
        @swiper="setControlledSwiper"
        @slideChange="onSlideChange"
      >
        <template v-for="i in tabs.length" :key="`tab-block-${i}`">
          <SwiperSlide>
            <div
              class="tab-container"
              :ref="`tab-${i - 1}`"
              :class="{
                'tab-container--active': currentTab === i,
              }"
            >
              <slot :name="`tab-item-${i}`" />
            </div>
          </SwiperSlide>
        </template>
      </Swiper>
    </div>
  </div>
</template>

<script>
import "swiper/swiper.scss";
import { Swiper, SwiperSlide } from "swiper/vue";
import TabSlider from "./tab-nav/TabSlider.vue";
import TabBlock from "./tab-nav/TabBlock.vue";

/*
Tab types:
  - slider
  - block
*/

const DEFAULT_SLIDER_TRANSITION = 250;

export default {
  components: {
    TabSlider,
    TabBlock,
    Swiper,
    SwiperSlide,
  },
  props: {
    // Tab names | Array<string>
    tabs: {
      type: Array,
      required: true,
    },
    tab: {
      type: Number,
      required: true,
    },
    type: {
      type: String,
      default: "slider",
    },
    shadowed: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:tab"],
  data() {
    return {
      currentTab: this.tab,
      swiper: null,
      sliderTransition: 0,
      progress: 0,
    };
  },
  mounted() {
    this.setHeight();
  },
  beforeUnmount() {
    this.swiper.off("touchStart", this.onTouchStart);
    this.swiper.off("touchEnd", this.onTouchEnd);
  },
  methods: {
    setHeight() {
      this.$nextTick(() => {
        window.scroll(0, 0);
        setTimeout(() => {
          const tab = this.$refs[`tab-${this.currentTab}`][0];
          if (tab) {
            const body = this.$refs.body;
            const height = tab.getBoundingClientRect().height;
            const top = tab.getBoundingClientRect().top;
            body.style.maxHeight = `${Math.max(
              height,
              window.innerHeight - top
            )}px`;
          }
        }, DEFAULT_SLIDER_TRANSITION);
      });
    },
    setProgress() {
      this.sliderTransition = DEFAULT_SLIDER_TRANSITION;
      requestAnimationFrame(() => {
        this.progress = this.swiper.progress;
      });
    },
    onTouchStart() {
      this.sliderTransition = 0;
    },
    onTouchEnd() {
      this.setProgress();
    },
    onTouchMove() {
      this.progress = this.swiper.progress;
    },
    onSlideChange({ activeIndex }) {
      this.currentTab = activeIndex;
    },
    setControlledSwiper(swiper) {
      this.swiper = swiper;
      this.progress = swiper.progress;
      this.swiper.on("touchStart", this.onTouchStart);
      this.swiper.on("touchEnd", this.onTouchEnd);
      this.swiper.on("touchMove", this.onTouchMove);
    },
  },
  watch: {
    currentTab(val) {
      this.$emit("update:tab", val);
    },
    tab(val) {
      this.currentTab = val;
      this.swiper.slideTo(val);
      this.setProgress();
      this.setHeight();
    },
    isLoading(val) {
      if (!val) {
        this.setHeight();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.tabs {
  font-family: Roboto-Regular;

  .tab-container {
    max-height: max-content;
  }

  &__head {
    &--shadowed {
      box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.15);
    }
  }

  &__body {
    overflow: hidden;
  }
}
</style>
